import { useCallback, useEffect, useRef, useState } from 'react'
import BN from 'bn.js'
import { PublicKey } from '@solana/web3.js'
import {
  createAssociatedTokenAccountIdempotentInstruction,
  createTransferInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import { getOperateIx, MAX_WITHDRAW_AMOUNT } from '@jup-ag/lend/borrow'
import { useAuth } from '../../auth/AuthContext'
import type { WalletBalance } from '../../types/wallet'
import { MINTS, type JupBorrowPortfolio } from '../../api/trade'
import { apiFetch } from '../../api/client'
import { newConnection } from '../../lib/tx'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { findBalance, formatBalance, formatUsdShort } from '../../hooks/useWalletBalance'
import { useSplBalance, findCanonicalAta } from '../../hooks/useSplBalance'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from './TradeResultBanner'

const JUPSOL_USDC_VAULT_ID = 13
const JUPSOL_DECIMALS = 9
// SOL needed before submit. Existing position: tx fees + jupSOL ATA rent (was
// 0.005, bumped to 0.01 to leave headroom for high priority fees during
// network congestion). Brand-new position: also funds the position PDA +
// position NFT mint + token account + Metaplex metadata, ~0.015 SOL on top.
const MIN_SOL_FOR_DEPOSIT      = 0.01
const MIN_SOL_FOR_INIT_DEPOSIT = 0.02
// Rent for a new SPL associated token account (~0.00203928 SOL, rounded up).
// Added to the minimum when the consolidation path creates the canonical ATA.
const ATA_RENT_SOL = 0.0025

interface CollateralPosition {
  vaultId: number
  positionId: number
  supplyAmount: number
  supplyUsd: number
  supplyApy: number
  borrowAmount: number
  borrowUsd: number
  borrowApr: number
}

/** String truncation to N decimal places — no floating-point, always ≤ input. */
function truncDec(dec: string, places = 4): string {
  const dot = dec.indexOf('.')
  if (dot === -1 || dec.length - dot - 1 <= places) return dec
  return dec.slice(0, dot + 1 + places)
}

function parseJup(raw: string): BN | null {
  if (!raw || !/^[0-9]*\.?[0-9]*$/.test(raw)) return null
  const [whole, frac = ''] = raw.split('.')
  const padded = (frac + '000000000').slice(0, JUPSOL_DECIMALS)
  try { return new BN((whole || '0') + padded) } catch { return null }
}

interface Props {
  balances: WalletBalance[] | null
}

/**
 * Collateral side of the Jupiter Lend BORROW position (Fluid vault).
 * Deposits/withdraws jupSOL against the same position NFT that BorrowForm
 * uses for USDC debt. NOT the earn-only vault (that's @jup-ag/lend/earn).
 */
export default function CollateralForm({ balances }: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, buildV0, publicKey, connected, friendlyTxError } = useAdapterSignAndSubmit()

  const [mode, setMode] = useState<'deposit' | 'withdraw'>('deposit')
  const [amount, setAmount] = useState('')
  const [withdrawAll, setWithdrawAll] = useState(false)
  const [pos, setPos] = useState<CollateralPosition | null>(null)
  const [apiError, setApiError] = useState(false)
  const [result, setResult] = useState<TradeResult>({ kind: 'idle' })

  // Display-only (shows merged wallet + staking total).
  const walletJupSol = findBalance(balances, MINTS.jupSOL)
  // Authoritative for tx building: reads the user's jupSOL ATA directly.
  const splJup = useSplBalance(publicKey, MINTS.jupSOL)
  // Tracks the exact raw BN returned by RPC when user clicks MAX, plus the
  // decimal string we wrote into the input — if the user hasn't edited since,
  // we use the raw BN at submit time so decimal↔BN round-trips don't push
  // the amount above the real ATA balance.
  const lastMaxRef = useRef<{ raw: BN; decimal: string } | null>(null)

  const reload = useCallback(async () => {
    if (!portfolioId) return
    let data: JupBorrowPortfolio | null
    try {
      data = await apiFetch<JupBorrowPortfolio>(
        `/api/jupiter/lend/portfolio-positions?user=${encodeURIComponent(portfolioId)}`,
      )
      setApiError(false)
    } catch {
      setApiError(true)
      return
    }
    if (!data?.elements) { setPos(null); return }
    const el = data.elements.find(e => e.type === 'borrowlend' && e.data?.link?.includes('/lend/borrow/'))
    if (!el?.data) { setPos(null); return }
    const link = el.data.link ?? ''
    const m = link.match(/\/borrow\/(\d+)\/nfts\/(\d+)/)
    const supply = el.data.suppliedAssets?.[0]?.data
    const borrow = el.data.borrowedAssets?.[0]?.data
    setPos({
      vaultId: m ? parseInt(m[1]) : JUPSOL_USDC_VAULT_ID,
      positionId: m ? parseInt(m[2]) : 0,
      supplyAmount: supply?.amount ?? 0,
      supplyUsd: (el.data.suppliedAssets?.[0] as any)?.value ?? 0,
      supplyApy: supply?.yields?.[0]?.apy ?? 0,
      borrowAmount: borrow?.amount ?? 0,
      borrowUsd: (el.data.borrowedAssets?.[0] as any)?.value ?? 0,
      borrowApr: Math.abs(el.data.borrowedYields?.[0]?.[0]?.apr ?? 0),
    })
  }, [portfolioId])

  useEffect(() => { reload() }, [reload])

  const submit = async () => {
    if (apiError) {
      setResult({ kind: 'error', message: 'Jupiter portfolio API is unreachable — cannot safely determine your existing position. Refresh and retry, or manage directly at jup.ag.' })
      return
    }
    const bnParsed = parseJup(amount)
    const isWithdrawAll = mode === 'withdraw' && withdrawAll
    if (!isWithdrawAll && (!bnParsed || bnParsed.isZero())) return
    setResult({ kind: 'pending', label: 'Building transaction…' })

    try {
      if (!connected || !publicKey) throw new Error('Wallet not connected')
      const signer = publicKey
      if (portfolioId && signer.toBase58() !== portfolioId) {
        throw new Error('Connected wallet does not match your session')
      }

      // Deposit pre-flight: if user clicked MAX and hasn't edited since, use
      // the exact raw BN from RPC (bypasses decimal truncation). Otherwise
      // validate the typed amount against the ATA balance first.
      let depositBn: BN | null = null
      if (mode === 'deposit') {
        if (lastMaxRef.current && amount === lastMaxRef.current.decimal) {
          depositBn = lastMaxRef.current.raw
        } else {
          depositBn = bnParsed
        }
        if (splJup.loading) {
          throw new Error('Wallet balance is still loading — wait a moment and retry.')
        }
        if (depositBn && depositBn.gt(splJup.totalRaw)) {
          const want = (Number(depositBn.toString()) / 1e9).toFixed(6)
          const have = (Number(splJup.totalRaw.toString()) / 1e9).toFixed(6)
          throw new Error(`Amount ${want} jupSOL exceeds available balance ${have} jupSOL. Click MAX to auto-populate.`)
        }
      }

      const connection = newConnection()
      const vaultId = pos?.vaultId ?? JUPSOL_USDC_VAULT_ID
      // positionId === 0 tells the SDK "no existing position — mint a new one";
      // it returns the init ix bundled into `ixs`. Calling getInitPositionIx
      // separately and passing the resulting nftId here makes getOperateIx try
      // to fetch a position account that doesn't yet exist (the init ix would
      // be in the same tx, not yet executed) and throw
      // "Account does not exist or has no data <PDA>".
      const positionId = pos?.positionId ?? 0

      // colAmount:  >0 = deposit jupSOL, <0 = withdraw jupSOL
      const colAmount = mode === 'deposit'
        ? (depositBn as BN)
        : (isWithdrawAll ? MAX_WITHDRAW_AMOUNT : (bnParsed as BN).neg())

      // Consolidation path: if the user's jupSOL is in a non-canonical account,
      // run TWO separate confirmed transactions to avoid the combined tx exceeding
      // what the Solana runtime allows (the SDK bundles its own account-init CPIs —
      // initTick, initBranch, initTickIdLiquidation — and mixing consolidation ixs
      // into the same tx causes a pre-simulation failure even with plenty of SOL).
      //   Tx 1: move jupSOL to canonical ATA (confirmed before Tx 2 starts)
      //   Tx 2: call getOperateIx — canonical ATA now has tokens, SDK builds clean
      if (mode === 'deposit' && splJup.needsConsolidation && depositBn != null && depositBn.gt(splJup.raw)) {
        const jupMint    = new PublicKey(MINTS.jupSOL)
        const canonicalAta = findCanonicalAta(signer, jupMint)
        const res = await connection.getParsedTokenAccountsByOwner(
          signer, { mint: jupMint }, 'confirmed',
        )
        const consolidateIxs = [
          createAssociatedTokenAccountIdempotentInstruction(
            signer, canonicalAta, signer, jupMint,
            TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID,
          ),
        ]
        let stillNeeded = depositBn.sub(splJup.raw)
        for (const acct of res.value) {
          if (stillNeeded.isZero()) break
          if (acct.pubkey.equals(canonicalAta)) continue
          const data = acct.account.data
          if (!('parsed' in data)) continue
          const acctBal = new BN(data.parsed?.info?.tokenAmount?.amount ?? '0')
          if (acctBal.isZero()) continue
          const toTransfer = stillNeeded.lte(acctBal) ? stillNeeded : acctBal
          consolidateIxs.push(
            createTransferInstruction(
              acct.pubkey, canonicalAta, signer, BigInt(toTransfer.toString()),
            ),
          )
          stillNeeded = stillNeeded.sub(toTransfer)
        }
        if (!stillNeeded.isZero()) {
          throw new Error('Insufficient jupSOL across all accounts — refresh and retry.')
        }

        // Tx 1: consolidate (wallet prompt 1/2)
        setResult({ kind: 'pending', label: '(1/2) Moving jupSOL to standard account — approve in wallet…' })
        const consolidateTx = await buildV0(consolidateIxs, [])
        await signAndSubmit(consolidateTx)

        // Tx 2: deposit — canonical ATA confirmed to have tokens now
        setResult({ kind: 'pending', label: '(2/2) Depositing jupSOL — approve in wallet…' })
        const { ixs: depositIxs, addressLookupTableAccounts: depositAlts } = await getOperateIx({
          vaultId,
          positionId,
          colAmount,
          debtAmount: new BN(0),
          connection,
          signer,
        })
        const depositTx = await buildV0(depositIxs, depositAlts)
        const submitted = await signAndSubmit(depositTx, { skipPreSimulation: true, skipPreflight: true })

        setResult({ kind: 'success', signature: submitted.signature, summary: `Deposited ${amount} jupSOL` })
        setAmount('')
        setWithdrawAll(false)
        lastMaxRef.current = null
        setTimeout(reload, 4000)
        return
      }

      // Normal path: no consolidation needed — single transaction
      const { ixs, addressLookupTableAccounts } = await getOperateIx({
        vaultId,
        positionId,
        colAmount,
        debtAmount: new BN(0),
        connection,
        signer,
      })

      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const tx = await buildV0(ixs, addressLookupTableAccounts)
      const submitted = await signAndSubmit(tx, { skipPreSimulation: true, skipPreflight: true })

      const display = isWithdrawAll ? 'all collateral' : `${amount} jupSOL`
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `${mode === 'deposit' ? 'Deposited' : 'Withdrew'} ${display}`,
      })
      setAmount('')
      setWithdrawAll(false)
      lastMaxRef.current = null
      setTimeout(reload, 4000)
    } catch (e) {
      console.error('[CollateralForm] TX error:', e)
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }

  const onMax = () => {
    if (mode === 'deposit') {
      if (splJup.totalRaw.isZero()) {
        setResult({ kind: 'error', message: 'Your wallet has zero jupSOL. Stake SOL first to obtain jupSOL.' })
        return
      }
      // Truncate (not round) to 4 decimal places so the field value is always
      // strictly less than the full on-chain balance. This prevents Phantom's
      // anti-drain scanner from flagging a 100%-balance transfer as suspicious.
      // Truncation guarantees parseJup(display) ≤ totalRaw so lastMaxRef is
      // not needed — submit can parse directly from the field.
      const display = truncDec(splJup.totalDecimal)
      setAmount(display)
      lastMaxRef.current = null
    } else if (pos) {
      setAmount(pos.supplyAmount.toFixed(4))
      lastMaxRef.current = null
    }
  }

  // Native-SOL balance for the rent / fees pre-check on deposit.
  // Read directly from RPC so a recent top-up is reflected immediately
  // (the backend polling cycle can lag by minutes).
  const splSol        = useSplBalance(publicKey, MINTS.SOL)
  const walletSol     = findBalance(balances, MINTS.SOL)  // display-only (USD value)
  // Use RPC balance when wallet is connected and value has loaded.
  // Fall back to the backend polling value while loading or when no wallet is connected.
  const solBalanceNum = (publicKey && !splSol.loading)
    ? Number(splSol.decimal)
    : parseFloat(walletSol?.amount ?? '0')
  const needsInitPosition  = !pos?.positionId
  const minSolNeeded       = (needsInitPosition ? MIN_SOL_FOR_INIT_DEPOSIT : MIN_SOL_FOR_DEPOSIT)
                             + (splJup.needsConsolidation ? ATA_RENT_SOL : 0)
  // Only show the SOL warning once the wallet is connected and the RPC balance has loaded.
  const insufficientSol    = mode === 'deposit' && !!publicKey && !splSol.loading && balances != null && solBalanceNum < minSolNeeded

  const canSubmit = !apiError && (mode === 'withdraw' && withdrawAll
    ? !!pos && pos.supplyAmount > 0
    : !!parseJup(amount) && !insufficientSol && !splJup.loading)

  return (
    <div className="space-y-3">
      {/* Same position cards as BorrowForm shows, so the numbers match across tabs. */}
      <div className="grid grid-cols-2 gap-2">
        <PosCard
          title="Collateral"
          mint={MINTS.jupSOL}
          symbol="jupSOL"
          amount={pos ? `${pos.supplyAmount.toFixed(3)}` : '—'}
          usd={pos ? `$${pos.supplyUsd.toFixed(2)}` : '—'}
          rate={pos && pos.supplyApy ? `+${(pos.supplyApy * 100).toFixed(1)}% APY` : ''}
          rateClass="text-accent-green"
        />
        <PosCard
          title="Debt"
          mint={MINTS.USDC}
          symbol="USDC"
          amount={pos ? `${pos.borrowAmount.toFixed(2)}` : '—'}
          usd={pos ? `$${pos.borrowUsd.toFixed(2)}` : '—'}
          rate={pos && pos.borrowApr ? `${(pos.borrowApr * 100).toFixed(1)}% APR` : ''}
          rateClass="text-accent-red"
        />
      </div>

      <div className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-[11px]">
        <div className="flex items-center gap-1.5 text-gray-500">
          <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={14} />
          Wallet jupSOL (spendable)
        </div>
        <div className="text-ink">
          {splJup.loading ? '…' : formatBalance(splJup.totalDecimal)} jupSOL
          {walletJupSol && (
            <span className="ml-1 text-gray-500">· {formatUsdShort(walletJupSol.usdValue)}</span>
          )}
        </div>
      </div>

      {apiError && (
        <div className="rounded-lg border border-accent-red/30 bg-accent-red/10 px-3 py-2 text-[11px] text-accent-red">
          Portfolio API unreachable — position data could not be loaded. Submitting is blocked to prevent creating a duplicate position. Refresh or retry in a moment.
        </div>
      )}

      {!apiError && !pos?.positionId && (
        <div className="rounded-lg border border-accent-yellow/30 bg-accent-yellow/10 px-3 py-2 text-[11px] text-accent-yellow">
          No borrow position found. The transaction will initialise one for the jupSOL/USDC vault.
        </div>
      )}

      {mode === 'deposit' && splJup.needsConsolidation && !splJup.totalRaw.isZero() && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-[11px] text-blue-300 leading-relaxed">
          Your jupSOL is in a non-standard token account. The transaction will automatically move it to your standard wallet account before depositing — no extra steps needed.
        </div>
      )}

      {insufficientSol && !splSol.loading && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
          Wallet has only {solBalanceNum.toFixed(4)} SOL — this {needsInitPosition ? 'init + deposit' : 'deposit'} needs at least
          {' '}{minSolNeeded} SOL on hand to fund tx fees{needsInitPosition ? ' plus the rent for the new position NFT, position PDA, token account and metadata' : ' plus any new associated token account rent'}. Top up SOL and retry.
        </div>
      )}

      <div className="flex gap-1 rounded-lg bg-surface-hover p-1">
        {(['deposit', 'withdraw'] as const).map(m => (
          <button key={m}
            onClick={() => { setMode(m); setAmount(''); setWithdrawAll(false) }}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${mode === m ? 'bg-accent-green/25 text-ink' : 'text-ink-muted'}`}
          >
            <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={14} />
            {m === 'deposit' ? 'Deposit collateral' : 'Withdraw collateral'}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-fringe bg-surface-hover p-3">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-wider text-gray-500">
            {mode === 'deposit' ? 'Deposit amount (jupSOL)' : 'Withdraw amount (jupSOL)'}
          </label>
          {((mode === 'deposit' && !splJup.totalRaw.isZero()) || (mode === 'withdraw' && pos && pos.supplyAmount > 0)) && (
            <button type="button" onClick={onMax} className="text-[10px] text-accent-green hover:underline">
              MAX {mode === 'deposit'
                ? truncDec(splJup.totalDecimal)
                : pos?.supplyAmount.toFixed(4)}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TokenIcon symbol="jupSOL" mint={MINTS.jupSOL} size={18} />
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.0"
            value={withdrawAll ? '' : amount}
            disabled={withdrawAll}
            onChange={e => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none disabled:text-gray-400"
          />
        </div>
        {mode === 'withdraw' && pos && pos.supplyAmount > 0 && (
          <label className="mt-1 flex items-center gap-2 text-[11px] text-ink-muted">
            <input type="checkbox" checked={withdrawAll} onChange={e => { setWithdrawAll(e.target.checked); if (e.target.checked) setAmount('') }} />
            Withdraw all collateral
          </label>
        )}
      </div>

      <button
        type="button"
        disabled={!canSubmit || result.kind === 'pending'}
        onClick={submit}
        className="w-full rounded-lg bg-accent-green py-2.5 text-sm font-semibold text-ink disabled:cursor-not-allowed disabled:bg-accent-green/40"
      >
        {result.kind === 'pending'
          ? 'Working…'
          : insufficientSol ? 'Top up SOL to deposit'
          : !connected ? (mode === 'deposit' ? 'Connect & Deposit' : 'Connect & Withdraw')
          : mode === 'deposit'
            ? (splJup.needsConsolidation ? 'Consolidate & Deposit (2 approvals)' : 'Sign & Deposit')
            : 'Sign & Withdraw'}
      </button>

      <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })} />
    </div>
  )
}

function PosCard({ title, mint, symbol, amount, usd, rate, rateClass }: {
  title: string; mint: string; symbol: string; amount: string; usd: string; rate: string; rateClass: string;
}) {
  return (
    <div className="rounded-lg bg-surface-hover p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{title}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <TokenIcon symbol={symbol} mint={mint} size={16} />
        <span className="text-sm font-semibold text-ink">{amount} {symbol}</span>
      </div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>{usd}</span>
        {rate && <span className={rateClass}>{rate}</span>}
      </div>
    </div>
  )
}
